import { useTraduccion } from '@tallerya/i18n';
import { AvisoError, Boton, CampoTexto, useAvisos, useFormulario, Ventana } from '@tallerya/ui';
import { useCambiosMarcas, version, type Marca, type Modelo } from './datos';

// Alta y renombre de un modelo, en una ventana. Un solo campo: **la marca es la
// que está elegida en la pantalla y no se cambia después** (ver `AssetModel`).

export type EdicionModelo = { tipo: 'nuevo'; marca: Marca } | { tipo: 'renombrar'; modelo: Modelo } | null;

interface Props {
  edicion: EdicionModelo;
  onCerrar: () => void;
  /** Tras crear un modelo: el formulario del activo (#119) lo deja elegido. */
  onCreado?: (id: string) => void;
}

export function VentanaModelo({ edicion, onCerrar, onCreado }: Props) {
  const clave = edicion === null ? 'cerrada' : edicion.tipo === 'nuevo' ? 'nuevo' : String(edicion.modelo.id);
  return <Contenido key={clave} edicion={edicion} onCerrar={onCerrar} {...(onCreado && { onCreado })} />;
}

function Contenido({ edicion, onCerrar, onCreado }: Props) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const cambios = useCambiosMarcas();
  const modelo = edicion?.tipo === 'renombrar' ? edicion.modelo : null;
  const marca = edicion?.tipo === 'nuevo' ? edicion.marca : null;

  const form = useFormulario<{ name: string }>({
    inicial: { name: modelo?.name ?? '' },
    validar: (v) => ({ ...(!v.name.trim() && { name: 'validation.required' }) }),
    guardar: (v) =>
      modelo
        ? cambios.renombrarModelo(String(modelo.id), version(modelo.version), v.name)
        : cambios.crearModelo(String(marca?.id ?? ''), v.name, onCreado),
    camposPorCodigo: { 'asset_model.nombre_duplicado': 'name' },
  });

  async function guardar() {
    const nombre = form.valores.name.trim();
    if (!(await form.enviar())) return;
    avisar({
      titulo: modelo
        ? t('admin.marcas.aviso.modeloRenombrado', { nombre })
        : t('admin.marcas.aviso.modeloCreado', { nombre }),
    });
    onCerrar();
  }

  function cerrar() {
    if (form.guardando) return;
    onCerrar();
  }

  return (
    <Ventana
      abierta={edicion !== null}
      titulo={modelo ? t('admin.marcas.ventanaModelo.renombrar') : t('admin.marcas.ventanaModelo.nuevo')}
      {...(marca && { info: t('admin.marcas.ventanaModelo.nuevoTexto', { marca: marca.name }) })}
      acciones={
        <>
          <Boton variante="texto" disabled={form.guardando} onClick={cerrar}>
            {t('app.cancelar')}
          </Boton>
          <Boton disabled={form.guardando} aria-busy={form.guardando} onClick={() => void guardar()}>
            {modelo ? t('admin.marcas.guardar') : t('admin.marcas.ventanaModelo.crear')}
          </Boton>
        </>
      }
    >
      {form.aviso && <AvisoError aviso={form.aviso} />}
      <CampoTexto
        form={form}
        nombre="name"
        etiqueta={t('admin.marcas.nombre')}
        obligatorio
        placeholder={t('admin.marcas.ventanaModelo.ejemplo')}
        autoComplete="off"
        ancho="completo"
      />
    </Ventana>
  );
}
